import { z } from '@/libs';
import {
  isValidEmail,
  isValidNameChars,
  isValidPhone,
  normalizeNameChars,
  parseDigits,
  validateCPF,
} from '@/utils';
import { removeDDIFromAutoInsert } from '@/utils/formatters';

export const customerRegistrationBaseSchema = z.object({
  document: z.string().trim(),
  fullname: z.string().trim(),
  socialName: z.string().trim().optional(),
  hasSocialName: z.boolean().optional(),
  email: z.string().trim().email('E-mail inválido'),
  phone: z.string().trim(),
  phoneConfirmation: z.string().trim(),
});

export const addFullnameValidationIssue = (
  value: string | undefined,
  ctx: z.RefinementCtx,
) => {
  if (!value || value.length < 6) {
    ctx.addIssue({
      path: ['fullname'],
      code: z.ZodIssueCode.too_small,
      type: 'string',
      minimum: 6,
      inclusive: true,
      message: 'Nome completo deve ter pelo menos 3 caracteres',
    });
  } else if (!isValidNameChars(value)) {
    ctx.addIssue({
      path: ['fullname'],
      code: z.ZodIssueCode.custom,
      message:
        'O campo contém caracteres não suportados. Use apenas letras, espaços, pontos, hífens e apóstrofos.',
    });
  }
};

const NAME_PARTICLES = new Set([
  'de',
  'da',
  'do',
  'das',
  'dos',
  'e',
  'di',
  'du',
  'del',
  'della',
  'la',
  'le',
  'van',
  'von',
  'y',
]);

export const addSocialNameValidationIssues = (
  value: string | undefined,
  ctx: z.RefinementCtx,
) => {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    ctx.addIssue({
      path: ['socialName'],
      code: z.ZodIssueCode.custom,
      message: 'Nome social não pode ser vazio',
    });
    return;
  }

  const hasDigits = /\d/.test(trimmed);
  if (hasDigits) {
    ctx.addIssue({
      path: ['socialName'],
      code: z.ZodIssueCode.custom,
      message: 'Nome social não pode conter caracteres numéricos',
    });
  }

  if (trimmed.includes('.')) {
    ctx.addIssue({
      path: ['socialName'],
      code: z.ZodIssueCode.custom,
      message: 'Nome social não pode conter ponto (.)',
    });
  }

  if (!hasDigits && !isValidNameChars(trimmed)) {
    ctx.addIssue({
      path: ['socialName'],
      code: z.ZodIssueCode.custom,
      message: 'Nome social não pode conter caracteres especiais',
    });
  }

  const parts = trimmed.split(/\s+/);
  const significantParts = parts.filter(
    (part, index) => index === 0 || !NAME_PARTICLES.has(part.toLowerCase()),
  );
  const everyPartHasMinLength = significantParts.every((part) => part.length >= 3);
  if (significantParts.length < 2 || !everyPartHasMinLength) {
    ctx.addIssue({
      path: ['socialName'],
      code: z.ZodIssueCode.custom,
      message: 'Nome social deve conter nome e sobrenome, cada um com no mínimo 3 letras',
    });
  }
};

export const documentFormSchema = z
  .object({
    document: z.string(),
  })
  .transform((data) => ({
    document: parseDigits(String(data.document)),
  }))
  .superRefine((data, ctx) => {
    const onValidateCPF = validateCPF(data.document);

    if (onValidateCPF) {
      ctx.addIssue({
        path: ['document'],
        code: z.ZodIssueCode.custom,
        message: onValidateCPF,
      });
    }
  });

export const phoneFormSchema = z
  .object({
    document: z.string().trim(),
    fullname: z.string().trim(),
    phone: z.string().trim(),
    email: z.string().trim().optional(),
  })
  .transform((data) => ({
    ...data,
    document: parseDigits(String(data.document)),
    fullname: normalizeNameChars(data.fullname),
    phone: removeDDIFromAutoInsert(parseDigits(data.phone)),
  }))
  .superRefine((data, ctx) => {
    const phoneError = isValidPhone(data.phone);
    if (phoneError) {
      ctx.addIssue({
        path: ['phone'],
        code: z.ZodIssueCode.custom,
        message: phoneError,
      });
    }

    if (!data.fullname || data.fullname.length < 3) {
      ctx.addIssue({
        path: ['fullname'],
        code: z.ZodIssueCode.too_small,
        type: 'string',
        minimum: 6,
        inclusive: true,
        message: 'Nome completo deve ter pelo menos 3 caracteres',
      });
    } else if (!isValidNameChars(data.fullname)) {
      ctx.addIssue({
        path: ['fullname'],
        code: z.ZodIssueCode.custom,
        message:
          'O campo contém caracteres não suportados. Use apenas letras, espaços, pontos, hífens e apóstrofos.',
      });
    }

    const emailProvided = typeof data.email === 'string' && data.email.length > 0;
    if (emailProvided && !isValidEmail(String(data.email))) {
      ctx.addIssue({
        path: ['email'],
        code: z.ZodIssueCode.custom,
        message: 'E-mail inválido',
      });
    }
  });

export const registrationFormSchema = customerRegistrationBaseSchema
  .extend({
    email: z.string().trim().optional(),
    MarketingOptIn: z.boolean().optional(),
    isTermsOfServiceAccepted: z.boolean().optional(),
  })
  .transform((data) => ({
    ...data,
    fullname: normalizeNameChars(data.fullname),
    socialName: data.socialName ? normalizeNameChars(data.socialName) : data.socialName,
  }))
  .superRefine((data, ctx) => {
    const emailProvided = typeof data.email === 'string' && data.email.length > 0;
    if (emailProvided && !isValidEmail(String(data.email))) {
      ctx.addIssue({
        path: ['email'],
        code: z.ZodIssueCode.custom,
        message: 'E-mail inválido',
      });
    }

    const rawPhone = removeDDIFromAutoInsert(parseDigits(data.phone));
    const rawPhoneConfirmation = removeDDIFromAutoInsert(
      parseDigits(data.phoneConfirmation),
    );

    const phoneError = isValidPhone(rawPhone);
    if (phoneError) {
      ctx.addIssue({
        path: ['phone'],
        code: z.ZodIssueCode.custom,
        message: phoneError,
      });
    }

    if (rawPhoneConfirmation.length === 0) {
      ctx.addIssue({
        path: ['phoneConfirmation'],
        code: z.ZodIssueCode.custom,
        message: 'Confirme o celular',
      });
    } else if (rawPhone !== rawPhoneConfirmation) {
      ctx.addIssue({
        path: ['phone'],
        code: z.ZodIssueCode.custom,
        message: 'Os telefones não coincidem',
      });
      ctx.addIssue({
        path: ['phoneConfirmation'],
        code: z.ZodIssueCode.custom,
        message: 'Os telefones não coincidem',
      });
    }

    addFullnameValidationIssue(data.fullname, ctx);

    if (data.hasSocialName) {
      addSocialNameValidationIssues(data.socialName, ctx);
    }
  })
  .transform((data) => ({
    ...data,
    document: parseDigits(String(data.document)),
    phone: removeDDIFromAutoInsert(parseDigits(data.phone)),
    phoneConfirmation: removeDDIFromAutoInsert(parseDigits(data.phoneConfirmation)),
  }));

export type DocumentFormData = z.infer<typeof documentFormSchema>;
export type PhoneFormData = z.infer<typeof phoneFormSchema>;
export type RegistrationFormData = z.input<typeof registrationFormSchema>;
